import React, { useMemo, useState, useEffect, useRef } from "react";

// =============================
// Picway Web App — Protótipo
// =============================
// - Tela de autenticação com senha fixa (pedido do cliente)
// - Após login, carrega o app de calendário + fluxo de trabalho
// - Mobile-first, 3 colunas (Para fazer / Em produção / Concluídas)

// ===== AVISO DE SEGURANÇA =====
// A senha fixa no front-end não é segura (o código fica visível no bundle).
// Use apenas para protótipo interno. Em produção, mova a validação para o backend.

const APP_PASSWORD = "557120"; // senha fixa (pedido do cliente)
const AUTH_KEY = "picway-auth-v1";
const verifyPassword = (pwd) => pwd === APP_PASSWORD;

// =============================
// Dados / Constantes do app
// =============================
const MOLDURAS = [
  "Tirinha 2 poses",
  "Tirinha 3 poses",
  "Tirinha 4 poses",
  "Retrato 1 pose",
  "Retrato 3 poses",
  "Paisagem tradicional",
  "Paisagem 3 poses",
  "Paisagem 2 poses",
  "Boomerang",
  "GIF",
];

const TOTEM_CABINE = [
  "Totem MDF 1 face",
  "Totem MDF inteiro",
  "Ring 1 face base",
  "Ring 1 face móvel",
  "Ring base inteiro",
  "Ring móvel inteiro",
  "Cabine inteira",
  "Cabine 1 face",
  "Fundo foto chroma-key",
  "Grua de prêmio",
  "Túnel de vento",
  "Túnel infinito laterais",
  "Totem interativo tela",
  "Totem interativo adesivo",
];

// Utils de data
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
const sameDay = (a,b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
const iso = (d) => d.toISOString().slice(0,10);

// Status
const STATUS = { TODO: "PARA_FAZER", PROD: "EM_PRODUCAO", DONE: "CONCLUIDO" };
const STATUS_COLOR = {
  TODO: { dot: "bg-yellow-500", soft: "bg-yellow-100", text: "text-yellow-800" },
  PROD: { dot: "bg-red-600", soft: "bg-red-100", text: "text-red-800" },
  DONE: { dot: "bg-green-600", soft: "bg-green-100", text: "text-green-800" },
  NONE: { dot: "bg-neutral-300", soft: "bg-white", text: "text-neutral-600" },
};
const dayAgg = (items = []) => {
  if (!items || items.length === 0) return "NONE";
  const hasProd = items.some((i) => i.status === STATUS.PROD);
  if (hasProd) return "PROD"; // prioridade ao vermelho
  const allDone = items.every((i) => i.status === STATUS.DONE);
  return allDone ? "DONE" : "TODO";
};
const nextStatus = (s) => (s===STATUS.TODO? STATUS.PROD : s===STATUS.PROD? STATUS.DONE : STATUS.TODO);

// Storage — agora persiste no SERVIDOR (todos veem as mesmas tarefas)
const rid = () => (typeof crypto!=="undefined" && crypto.randomUUID? crypto.randomUUID(): Math.random().toString(36).slice(2));

// API simples em PHP (api/tasks.php)
const apiGetTasks = async () => {
  try {
    const r = await fetch('/api/tasks.php', { credentials: 'include' });
    if (!r.ok) throw new Error('fail');
    return await r.json();
  } catch {
    return {};
  }
};
const apiSetTasks = async (data) => {
  try {
    await fetch('/api/tasks.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ data })
    });
  } catch {}
};

// Helper para exibir molduras + totem/cabine
const displayFrames = (item) => {
  const a = Array.isArray(item.molduras)? item.molduras: [];
  const b = Array.isArray(item.totemCabine)? item.totemCabine: [];
  const merged = [...a, ...b];
  return merged.length ? merged.join(", ") : "";
};

// ===================================
// Shell com Autenticação
// ===================================
export default function PicwayAppShell(){
  const [authed, setAuthed] = useState(()=>{
    try { return localStorage.getItem(AUTH_KEY)==="1"; } catch { return false; }
  });

  const handleLogin = (pwd) => {
    if(verifyPassword(pwd)){
      try { localStorage.setItem(AUTH_KEY, "1"); } catch {}
      setAuthed(true);
    } else {
      alert("Senha incorreta. Tente novamente.");
    }
  };

  const handleLogout = () => {
    try { localStorage.removeItem(AUTH_KEY); } catch {}
    setAuthed(false);
  };

  if(!authed){
    return <AuthScreen onSubmit={handleLogin} />;
  }

  return <PicwayCadastroArtes onLogout={handleLogout} />;
}

// ===================================
// App principal (o que já existia antes)
// ===================================
function PicwayCadastroArtes({ onLogout }){
  const today = new Date();
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [tasks, setTasks] = useState({}); // { [yyyy-mm-dd]: Array<Arte> }
  const [dayModalDate, setDayModalDate] = useState(null); // controla o pop-up do dia
  const [editing, setEditing] = useState(null); // controla o modal de edição
  const saveTimer = useRef(null);

  // carregar do servidor ao abrir
  useEffect(()=>{
    let alive = true;
    (async()=>{
      const data = await apiGetTasks();
      if (alive) setTasks(data || {});
    })();
    return ()=>{ alive = false; };
  },[]);

  // salvar no servidor (debounce) sempre que tasks mudar
  useEffect(()=>{
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(()=>{ apiSetTasks(tasks); }, 500);
  },[tasks]);

  const matrix = useMemo(()=>{
    const start = startOfMonth(month);
    const startWeekday = (start.getDay()+6)%7; // Mon=0
    const gridStart = addDays(start,-startWeekday);
    return Array.from({length:42},(_,i)=>addDays(gridStart,i));
  },[month]);

  const monthLabel = month.toLocaleDateString("pt-BR",{month:"long",year:"numeric"});

  const upsertDay = (dateKey, arr) => setTasks(prev=>({...prev,[dateKey]:arr}));
  const addNewItem = (dateKey) => {
    const item = { id: rid(), cliente:"", molduras:[], totemCabine:[], descricao:"", status: STATUS.TODO, driveLink:"" };
    const arr = Array.isArray(tasks[dateKey])? [...tasks[dateKey], item] : [item];
    upsertDay(dateKey, arr);
    setEditing({ dateKey, item }); // abre modal da arte nova
  };
  const updateItem = (dateKey, updatedItem) => {
    const arr = (tasks[dateKey]||[]).map(it=> it.id===updatedItem.id? updatedItem: it);
    upsertDay(dateKey, arr);
  };
  const setStatus = (dateKey, itemId, status) => {
    const arr = (tasks[dateKey]||[]).map(it=> it.id===itemId? {...it, status}: it);
    upsertDay(dateKey, arr);
  };

  // Flatten helpers para as 3 colunas
  const entriesTodo = useMemo(()=>{
    const out = [];
    Object.entries(tasks).forEach(([dateKey, arr])=>{ (arr||[]).forEach((it)=>{ if(it.status===STATUS.TODO) out.push({dateKey, item: it}); }); });
    return out.sort((a,b)=> new Date(b.dateKey) - new Date(a.dateKey));
  },[tasks]);
  const entriesProd = useMemo(()=>{
    const out = [];
    Object.entries(tasks).forEach(([dateKey, arr])=>{ (arr||[]).forEach((it)=>{ if(it.status===STATUS.PROD) out.push({dateKey, item: it}); }); });
    return out.sort((a,b)=> new Date(b.dateKey) - new Date(a.dateKey));
  },[tasks]);
  const entriesDone = useMemo(()=>{
    const out = [];
    Object.entries(tasks).forEach(([dateKey, arr])=>{ (arr||[]).forEach((it)=>{ if(it.status===STATUS.DONE) out.push({dateKey, item: it}); }); });
    return out.sort((a,b)=> new Date(b.dateKey) - new Date(a.dateKey));
  },[tasks]);

  return (
    <div className="min-h-screen bg-neutral-50 pb-[calc(env(safe-area-inset-bottom)+16px)]">
      <header className="sticky top-0 z-10 backdrop-blur bg-white/80 border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-gradient-to-br from-black to-neutral-700 flex items-center justify-center text-white font-semibold shadow">PW</div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-semibold leading-tight truncate">Picway — Cadastro de Artes</h1>
              <p className="text-[11px] sm:text-sm text-neutral-600">Calendário + artes do dia</p>
            </div>
          </div>
          <button onClick={onLogout} className="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm hover:bg-white">Sair</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()-1,1))} className="px-3 py-2 rounded-xl border border-neutral-200 hover:bg-white text-sm">← Mês anterior</button>
            <button onClick={()=>setMonth(new Date(today.getFullYear(),today.getMonth(),1))} className="px-3 py-2 rounded-xl border border-neutral-200 hover:bg-white text-sm">Hoje</button>
            <button onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()+1,1))} className="px-3 py-2 rounded-xl border border-neutral-200 hover:bg-white text-sm">Próximo mês →</button>
          </div>
          <div className="text-base sm:text-lg font-medium capitalize">{monthLabel}</div>
        </div>

        <div className="mt-3 sm:mt-4 flex items-center gap-4 text-xs sm:text-sm">
          <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500" /> Para fazer</span>
          <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-600" /> Em produção</span>
          <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-600" /> Concluído</span>
        </div>

        {/* Calendário */}
        <section className="mt-3 sm:mt-4 bg-white rounded-2xl shadow border border-neutral-200 p-3 sm:p-4">
          <div className="grid grid-cols-7 text-center text-[10px] sm:text-xs font-medium text-neutral-500">
            {"SEG TER QUA QUI SEX SÁB DOM".split(" ").map((d)=>(<div key={d} className="py-1.5 sm:py-2 uppercase tracking-wide">{d}</div>))}
          </div>
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {matrix.map((day, idx)=>{
              const inMonth = day.getMonth()===month.getMonth();
              const key = iso(day);
              const items = tasks[key]||[];
              const agg = dayAgg(items);
              const color = STATUS_COLOR[agg];
              const isToday = sameDay(day,new Date());
              return (
                <button key={idx} onClick={()=>setDayModalDate(key)}
                  className={["relative h-20 sm:h-24 rounded-xl border text-left p-1.5 sm:p-2 transition focus:outline-none focus:ring-2",
                    inMonth?"bg-white":"bg-neutral-50 text-neutral-400",
                    color.soft,
                    agg!=="NONE"?`${color.text} border-transparent`:"border-neutral-200",
                    isToday?"ring-2 ring-neutral-900":"focus:ring-neutral-400"].join(" ")}
                  title={items.length?`${items.length} arte(s)`: "Cadastrar tarefas"}>
                  <div className="flex items-start justify-between">
                    <div className="text-xs sm:text-sm font-semibold">{day.getDate()}</div>
                    <div className="flex items-center gap-1">{agg!=="NONE" && <span className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${color.dot}`} />}</div>
                  </div>
                  {items.length>0 && (
                    <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-[11px] leading-snug line-clamp-3">
                      <div className="font-medium">{items.slice(0,2).map((it,i)=>(<span key={it.id}>{it.cliente||"(sem cliente)"}{i<Math.min(items.length,2)-1?", ":""}</span>))}{items.length>2?"…":""}</div>
                      <div className="opacity-80">{items.length} arte(s)</div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* 3 Colunas abaixo do calendário */}
        <section className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Coluna 1 — Para fazer */}
          <div className="bg-white rounded-2xl shadow border border-neutral-200 overflow-hidden">
            <div className="px-3 sm:px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-sm sm:text-base font-semibold">Artes para fazer</h3>
              <span className="text-xs text-neutral-500">Mover ➜ produção</span>
            </div>
            <ItemList
              entries={entriesTodo}
              emptyText="Nenhuma arte pendente."
              primaryActionLabel="Produção"
              primaryClass="bg-red-600"
              onPrimaryAction={(dateKey, id)=> setStatus(dateKey, id, STATUS.PROD)}
              onOpenItem={(dateKey, item)=> setEditing({dateKey, item})}
              onOpenDay={(dateKey)=> setDayModalDate(dateKey)}
            />
          </div>

          {/* Coluna 2 — Em produção */}
          <div className="bg-white rounded-2xl shadow border border-neutral-200 overflow-hidden">
            <div className="px-3 sm:px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-sm sm:text-base font-semibold">Artes em produção</h3>
              <span className="text-xs text-neutral-500">Mover ➜ concluídas</span>
            </div>
            <ItemList
              entries={entriesProd}
              emptyText="Nenhuma arte em produção."
              primaryActionLabel="Concluído"
              primaryClass="bg-green-600"
              onPrimaryAction={(dateKey, id)=> setStatus(dateKey, id, STATUS.DONE)}
              onOpenItem={(dateKey, item)=> setEditing({dateKey, item})}
              onOpenDay={(dateKey)=> setDayModalDate(dateKey)}
            />
          </div>

          {/* Coluna 3 — Concluídas */}
          <div className="bg-white rounded-2xl shadow border border-neutral-200 overflow-hidden">
            <div className="px-3 sm:px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-sm sm:text-base font-semibold">Artes concluídas</h3>
              <span className="text-xs text-neutral-500">Reabrir ➜ para fazer</span>
            </div>
            <ItemList
              entries={entriesDone}
              emptyText="Nenhuma arte concluída ainda."
              primaryActionLabel="Reabrir"
              primaryClass="bg-yellow-500"
              onPrimaryAction={(dateKey, id)=> setStatus(dateKey, id, STATUS.TODO)}
              onOpenItem={(dateKey, item)=> setEditing({dateKey, item})}
              onOpenDay={(dateKey)=> setDayModalDate(dateKey)}
            />
          </div>
        </section>
      </main>

      {/* POP-UP de dia: lista de tarefas daquela data + botão de cadastrar nova */}
      {dayModalDate && (
        <DayTasksModal
          dateKey={dayModalDate}
          items={tasks[dayModalDate] || []}
          onClose={()=>setDayModalDate(null)}
          onCreate={()=>{ addNewItem(dayModalDate); setDayModalDate(null); }}
          onOpenItem={(item)=>{ setDayModalDate(null); setEditing({dateKey: dayModalDate, item}); }}
          onAdvanceStatus={(itemId)=>{ // avança para a próxima coluna
            const arr = (tasks[dayModalDate]||[]).map(it=> it.id===itemId? {...it, status: nextStatus(it.status)}: it);
            upsertDay(dayModalDate, arr);
          }}
        />
      )}

      {/* Modal: Edição individual */}
      {editing && (
        <EditModal
          dateKey={editing.dateKey}
          item={editing.item}
          onClose={()=>setEditing(null)}
          onSave={(updated)=>{ updateItem(editing.dateKey, updated); setEditing(null); }}
        />
      )}

      <footer className="max-w-6xl mx-auto px-3 sm:px-4 pb-8 text-xs text-neutral-500">Feito para a Picway · Protótipo UI — fluxo "Cadastro de Artes"</footer>
    </div>
  );
}

// =============================
// Componentes auxiliares
// =============================
function ItemList({ entries, emptyText, primaryActionLabel, primaryClass, onPrimaryAction, onOpenItem, onOpenDay }){
  if (!entries.length) return <div className="p-4 text-sm text-neutral-500">{emptyText}</div>;
  return (
    <div className="divide-y divide-neutral-200">
      {entries.map(({dateKey, item})=>{
        const label = new Date(dateKey+"T00:00:00").toLocaleDateString("pt-BR",{ day:"2-digit", month:"2-digit", year:"numeric" });
        return (
          <div key={item.id} className="flex items-center gap-3 px-3 sm:px-4 py-3">
            <button onClick={()=>onOpenDay(dateKey)} className="text-xs px-2 py-1 border border-neutral-300 rounded-lg bg-white hover:bg-neutral-50 shrink-0" title="Abrir pop-up do dia">{label}</button>
            <button onClick={()=>onOpenItem(dateKey, item)} className="flex-1 text-left truncate">
              <div className="text-sm font-medium truncate">{item.cliente || "(sem cliente)"}</div>
              <div className="text-xs text-neutral-500 truncate">{displayFrames(item)}</div>
            </button>
            <button onClick={()=>onPrimaryAction(dateKey, item.id)} className={`text-xs px-2 py-1 rounded-lg text-white hover:opacity-90 ${primaryClass}`}>
              {primaryActionLabel}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function DayTasksModal({dateKey, items, onClose, onCreate, onOpenItem, onAdvanceStatus}){
  const label = new Date(dateKey+"T00:00:00").toLocaleDateString("pt-BR",{ weekday:"long", day:"2-digit", month:"long", year:"numeric" });
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40">
      <div className="w-full sm:max-w-xl bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-neutral-200 overflow-hidden">
        <div className="p-3 sm:p-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-semibold capitalize">{label}</h2>
            <p className="text-xs text-neutral-600">Artes cadastradas nesse dia</p>
          </div>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg bg-neutral-100 hover:bg-neutral-200">Fechar</button>
        </div>

        <div className="max-h-[70vh] overflow-auto divide-y divide-neutral-200">
          {(!items || !items.length) ? (
            <div className="p-4 text-sm text-neutral-500">Nenhuma arte cadastrada para esta data.</div>
          ) : items.map((it)=> {
            const labelBtn = it.status===STATUS.TODO? "Produção" : it.status===STATUS.PROD? "Concluído" : "Reabrir";
            const cls = it.status===STATUS.TODO? "bg-red-600" : it.status===STATUS.PROD? "bg-green-600" : "bg-yellow-500";
            return (
              <div key={it.id} className="flex items-center gap-3 px-4 py-3">
                <button
                  onClick={()=>onAdvanceStatus(it.id)}
                  title={`Mover ➜ ${labelBtn}`}
                  className={`text-xs px-2 py-1 rounded-lg text-white hover:opacity-90 ${cls}`}
                >
                  {labelBtn}
                </button>
                <button onClick={()=>onOpenItem(it)} className="flex-1 text-left truncate">
                  <div className="text-sm font-medium truncate">{it.cliente || "(sem cliente)"}</div>
                  <div className="text-xs text-neutral-500 truncate">{displayFrames(it)}</div>
                </button>
              </div>
            );
          })}
        </div>

        <div className="p-3 sm:p-4 border-t border-neutral-200 flex justify-end">
          <button onClick={onCreate} className="px-4 py-2 rounded-xl bg-neutral-900 text-white hover:opacity-90 text-sm">Cadastrar nova arte</button>
        </div>
      </div>
    </div>
  );
}

function EditModal({dateKey, item, onClose, onSave}){
  const [form, setForm] = useState(item);
  const label = new Date(dateKey+"T00:00:00").toLocaleDateString("pt-BR",{ weekday:"long", day:"2-digit", month:"long", year:"numeric" });
  const setF = (k,v)=> setForm(p=>({...p,[k]:v}));
  const remaining = 1000 - (form.descricao?.length||0);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40">
      <div className="w-full h-[92vh] sm:h-auto sm:max-w-3xl bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-neutral-200 overflow-hidden">
        <div className="p-3 sm:p-6 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold truncate">Cadastro de arte — {label}</h2>
            <p className="text-xs sm:text-sm text-neutral-600 truncate">Preencha os campos e salve</p>
          </div>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg bg-neutral-100 hover:bg-neutral-200">Fechar</button>
        </div>

        <div className="p-3 sm:p-6 space-y-6 max-h-[calc(92vh-112px)] sm:max-h-[70vh] overflow-auto">
          {/* Cliente */}
          <section>
            <label className="text-sm font-medium mb-2 block">Cliente</label>
            <input type="text" value={form.cliente||""} onChange={(e)=>setF("cliente", e.target.value)} placeholder="Nome do cliente" className="w-full rounded-xl border border-neutral-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400 bg-white" />
          </section>

          {/* Link do Drive */}
          <section>
            <label className="text-sm font-medium mb-2 block">Link da pasta no Google Drive</label>
            <input type="url" value={form.driveLink||""} onChange={(e)=>setF("driveLink", e.target.value)} placeholder="https://drive.google.com/drive/folders/..." className="w-full rounded-xl border border-neutral-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400 bg-white" />
          </section>

          {/* Molduras + Totem/Cabine */}
          <section>
            <h4 className="text-sm font-medium mb-3">Molduras</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Coluna 1: Molduras (existentes) */}
              <div>
                <h5 className="text-xs font-semibold uppercase tracking-wide mb-2 text-neutral-600">Padrão</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {MOLDURAS.map((m)=>{
                    const checked = (form.molduras||[]).includes(m);
                    return (
                      <label key={m} className={`flex items-center gap-2 p-2 rounded-xl border cursor-pointer select-none ${checked?"border-neutral-900 bg-neutral-50":"border-neutral-200 hover:bg-neutral-50"}`}>
                        <input type="checkbox" className="accent-neutral-900" checked={checked} onChange={()=>{
                          const set = new Set(form.molduras||[]);
                          set.has(m) ? set.delete(m) : set.add(m);
                          setF("molduras", Array.from(set));
                        }} />
                        <span className="text-sm">{m}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Coluna 2: Totem/Cabine */}
              <div>
                <h5 className="text-xs font-semibold uppercase tracking-wide mb-2 text-neutral-600">Totem/Cabine</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {TOTEM_CABINE.map((m)=>{
                    const checked = (form.totemCabine||[]).includes(m);
                    return (
                      <label key={m} className={`flex items-center gap-2 p-2 rounded-xl border cursor-pointer select-none ${checked?"border-neutral-900 bg-neutral-50":"border-neutral-200 hover:bg-neutral-50"}`}>
                        <input type="checkbox" className="accent-neutral-900" checked={checked} onChange={()=>{
                          const set = new Set(form.totemCabine||[]);
                          set.has(m) ? set.delete(m) : set.add(m);
                          setF("totemCabine", Array.from(set));
                        }} />
                        <span className="text-sm">{m}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* Descrição */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Descrição</h4>
              <div className={`text-xs ${ remaining<0?"text-red-600":"text-neutral-500"}`}>{remaining} caracteres restantes</div>
            </div>
            <textarea value={form.descricao||""} onChange={(e)=>setF("descricao", e.target.value.slice(0,1000))} className="w-full min-h-[100px] rounded-xl border border-neutral-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400 bg-white" placeholder="Detalhes da arte, referências, observações… (máx. 1000 caracteres)" maxLength={1000} />
          </section>

          {/* Status */}
          <section className="space-y-2">
            <h4 className="text-sm font-medium">Status</h4>
            <div className="flex flex-wrap gap-2">
              <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-neutral-300 cursor-pointer bg-white">
                <input type="radio" name="status" value={STATUS.TODO} checked={form.status===STATUS.TODO} onChange={()=>setF("status", STATUS.TODO)} className="accent-yellow-500" />
                <span className="text-sm">Para fazer</span>
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 ml-1" />
              </label>
              <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-neutral-300 cursor-pointer bg-white">
                <input type="radio" name="status" value={STATUS.PROD} checked={form.status===STATUS.PROD} onChange={()=>setF("status", STATUS.PROD)} className="accent-red-600" />
                <span className="text-sm">Em produção</span>
                <span className="w-2.5 h-2.5 rounded-full bg-red-600 ml-1" />
              </label>
              <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-neutral-300 cursor-pointer bg-white">
                <input type="radio" name="status" value={STATUS.DONE} checked={form.status===STATUS.DONE} onChange={()=>setF("status", STATUS.DONE)} className="accent-green-600" />
                <span className="text-sm">Concluído</span>
                <span className="w-2.5 h-2.5 rounded-full bg-green-600 ml-1" />
              </label>
            </div>
          </section>
        </div>

        {/* Ações */}
        <div className="p-3 sm:p-6 border-t border-neutral-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={()=>onClose()} className="px-4 py-2 rounded-xl border border-neutral-300 text-neutral-700 hover:bg-neutral-50">Cancelar</button>
          <button onClick={()=>onSave(form)} className="px-4 py-2 rounded-xl bg-neutral-900 text-white shadow hover:opacity-90">Salvar</button>
        </div>
      </div>
    </div>
  );
}

// =============================
// Tela de Autenticação (senha única)
// =============================
function AuthScreen({ onSubmit }){
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);

  const submit = () => onSubmit(pwd);
  const onKey = (e) => { if(e.key === "Enter") submit(); };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white border border-neutral-200 rounded-2xl shadow p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-black to-neutral-700 flex items-center justify-center text-white font-semibold">PW</div>
          <div>
            <h1 className="text-lg font-semibold">Picway — Acesso</h1>
            <p className="text-xs text-neutral-600">Digite a senha para entrar</p>
          </div>
        </div>

        <label className="text-sm font-medium">Senha</label>
        <div className="mt-1 flex items-center gap-2">
          <input
            type={show? "text" : "password"}
            value={pwd}
            onChange={(e)=>setPwd(e.target.value)}
            onKeyDown={onKey}
            placeholder="••••••"
            className="flex-1 rounded-xl border border-neutral-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400"
          />
          <button onClick={()=>setShow(s=>!s)} className="px-3 py-2 rounded-xl border border-neutral-300 text-xs hover:bg-neutral-50">{show? "Ocultar" : "Mostrar"}</button>
        </div>

        <button onClick={submit} className="mt-4 w-full px-4 py-2 rounded-xl bg-neutral-900 text-white text-sm hover:opacity-90">Entrar</button>
        <p className="mt-3 text-[11px] text-neutral-500">* Protótipo — senha fixa definida pelo cliente.</p>
      </div>
    </div>
  );
}

// =====================
// Testes mínimos (Console)
// =====================
(function runTests(){
  try{
    const tests = [
      {name:"STATUS keys", pass: !!(STATUS.TODO && STATUS.PROD && STATUS.DONE)},
      {name:"dayAgg NONE", pass: dayAgg([])==="NONE"},
      {name:"dayAgg TODO", pass: dayAgg([{status:STATUS.TODO}])==="TODO"},
      {name:"dayAgg PROD", pass: dayAgg([{status:STATUS.PROD},{status:STATUS.TODO}])==="PROD"},
      {name:"dayAgg DONE", pass: dayAgg([{status:STATUS.DONE},{status:STATUS.DONE}])==="DONE"},
      {name:"iso format", pass: /^\d{4}-\d{2}-\d{2}$/.test(iso(new Date()))},
      {name:"nextStatus TODO->PROD", pass: nextStatus(STATUS.TODO)===STATUS.PROD},
      {name:"nextStatus PROD->DONE", pass: nextStatus(STATUS.PROD)===STATUS.DONE},
      {name:"nextStatus DONE->TODO", pass: nextStatus(STATUS.DONE)===STATUS.TODO},
    ];
    console.table(tests);
  }catch(e){ console.log("Tests error", e); }
})();
